// DiagnosticFinPage — Page de finalisation / célébration
// Layout : Stats résumées + Card héros Plan d'action (3/5) + Card Documents (2/5)

import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Card, CardContent, Button } from '../components/ui'
import { trackEvent } from '../lib/analytics'
import { DiagnosticLayout, useDiagnosticSteps, useDiagnosticData, useDiagnosticProgress } from '../components/layout'
import { api, type SaveActionPlanInput } from '../services/api'
import {
  CheckCircle2, ClipboardList, FileText,
  ArrowRight, Loader2, WifiOff,
} from 'lucide-react'
import { cn } from '../lib/utils'
import { getCasques } from '@autodiag/shared'
import { CasqueIcons } from '../components/results/CasqueDisplay'

interface DiagnosticResult {
  scoreGlobal: number
  maxScore: number
  casques: number
}

interface SolutionRef {
  id: string
  code?: string
  theme: string
  libelle: string
  suggestion?: string
  metierId?: string
}

interface LocationState {
  selectedThemes?: string[]
  selectedSolutions?: Array<{ solutionId: string; metierIds: string[] }>
  [key: string]: unknown
}

// État de la card plan d'action
type PlanState = 'fresh' | 'exists' | 'empty'

// ── Composant StatBlock ──
function StatBlock({ value, label }: {
  value: number | string
  label: string
}) {
  return (
    <div className="flex flex-col items-center px-4 py-2">
      <p className="text-2xl font-bold tabular-nums text-primary">{value}</p>
      <p className="text-[11px] text-muted-foreground text-center leading-tight mt-0.5">{label}</p>
    </div>
  )
}

export default function DiagnosticFinPage() {
  const { id: diagnosticId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const { metierIds, metiersSelectionnes, diagnosticState } = useDiagnosticData(diagnosticId)
  const diagnosticProgress = useDiagnosticProgress(diagnosticId)

  const [result, setResult] = useState<DiagnosticResult | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // État plan d'action
  const [planState, setPlanState] = useState<PlanState>('empty')
  const [existingActionsCount, setExistingActionsCount] = useState(0)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false)
  const [pdfError, setPdfError] = useState<string | null>(null)

  // Données de navigation (thèmes et solutions sélectionnées)
  const locationState = location.state as LocationState | undefined
  const [selectedSolutions, setSelectedSolutions] = useState<Array<{ solutionId: string; metierIds: string[] }>>(
    locationState?.selectedSolutions || []
  )
  const [selectedThemes, setSelectedThemes] = useState<string[]>(
    locationState?.selectedThemes || []
  )
  const themesCount = selectedThemes.length
  const actionsCount = selectedSolutions.length

  // Animation cascade step-based
  const [step, setStep] = useState(0)

  const steps = useDiagnosticSteps('fin', diagnosticId, {
    totalMetiers: metierIds.length || 1,
    currentMetierIndex: Math.max(0, metierIds.length - 1),
    q2Completed: true,
    metiersSelectionnes: metiersSelectionnes.map(m => ({ code: m.code, nom: m.nom })),
  }, diagnosticProgress)

  // Charger résultats + vérifier si des actions existent déjà en DB
  useEffect(() => {
    async function loadData() {
      if (!diagnosticId) return
      setIsLoading(true)
      try {
        const [diagnostic, existingActions] = await Promise.all([
          api.getDiagnostic(diagnosticId),
          api.getActionPlans(diagnosticId),
        ])

        if (diagnostic.resultat) {
          setResult({
            scoreGlobal: diagnostic.resultat.scoreGlobal || 0,
            maxScore: diagnostic.resultat.maxGlobal || diagnostic.resultat.maxScore || 0,
            casques: diagnostic.resultat.casques || 1,
          })
        } else if (diagnostic.reponses && diagnostic.reponses.length > 0) {
          // Calcul à la volée si le diagnostic n'a pas encore été "terminé"
          const reponses = diagnostic.reponses as Array<{ valeur: number }>
          const scoreGlobal = reponses.reduce((sum: number, r: { valeur: number }) => sum + r.valeur, 0)
          const nbMetiers = Math.min(3, Math.max(1, diagnostic.metiers?.length || 1)) as 1 | 2 | 3
          const maxScore = reponses.length * 3
          const computedCasques = getCasques(scoreGlobal, nbMetiers)
          setResult({ scoreGlobal, maxScore, casques: computedCasques })
        }

        // Restaurer sélections depuis la BDD si pas de location.state
        let currentSolutions = selectedSolutions
        if (currentSolutions.length === 0) {
          const savedSolutions = diagnostic?.selectedSolutions as Array<{ solutionId: string; metierIds: string[] }> | null
          if (savedSolutions && savedSolutions.length > 0) {
            setSelectedSolutions(savedSolutions)
            currentSolutions = savedSolutions
          }
        }
        if (selectedThemes.length === 0) {
          const savedThemes = diagnostic?.selectedThemes as string[] | null
          if (savedThemes && savedThemes.length > 0) {
            setSelectedThemes(savedThemes)
          }
        }

        // Déterminer l'état de la card plan d'action
        if (existingActions.length > 0) {
          setPlanState('exists')
          setExistingActionsCount(existingActions.length)
        } else if (currentSolutions.length > 0) {
          setPlanState('fresh')
        } else {
          setPlanState('empty')
        }
      } catch (error) {
        console.error('Erreur chargement résultats:', error)
      } finally {
        setIsLoading(false)
      }
    }
    loadData()
  }, [diagnosticId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Animation cascade après chargement
  useEffect(() => {
    if (isLoading) return
    const timers = [
      setTimeout(() => setStep(1), 100),
      setTimeout(() => setStep(2), 400),
      setTimeout(() => setStep(3), 600),
      setTimeout(() => setStep(4), 800),
      setTimeout(() => setStep(5), 1000),
      setTimeout(() => setStep(6), 1200),
    ]
    return () => timers.forEach(clearTimeout)
  }, [isLoading])

  const show = (n: number) => step >= n

  // Voir le plan d'action — sauvegarde silencieuse si fresh, puis redirige
  const handleGeneratePlan = useCallback(async () => {
    if (!diagnosticId) return
    trackEvent('voir plan action')

    // Si le plan existe et pas de nouvelles solutions à fusionner → rediriger directement
    if (planState === 'exists' && selectedSolutions.length === 0) {
      navigate('/dashboard/plan-action')
      return
    }

    if (selectedSolutions.length === 0) return

    if (!navigator.onLine) {
      setSaveError('Connexion requise pour sauvegarder votre plan')
      return
    }

    setIsSaving(true)
    setSaveError(null)

    try {
      const response = await fetch('/data/referentiel/solutions.json')
      const solutionsData = await response.json()
      const solutionsRef: SolutionRef[] = solutionsData.data || []

      const actions: SaveActionPlanInput[] = selectedSolutions.map(sel => {
        const solution = solutionsRef.find(s => s.id === sel.solutionId)
        return {
          solutionId: sel.solutionId,
          metierCodes: sel.metierIds.length > 0 ? sel.metierIds : metierIds,
          theme: solution?.theme || 'Autre',
          libelle: solution?.libelle || solution?.suggestion || sel.solutionId,
          statut: 'a_faire' as const,
        }
      })

      await api.saveActionPlans(diagnosticId, { actions })
      navigate('/dashboard/plan-action')
    } catch (error) {
      console.error('Erreur sauvegarde plan:', error)
      setSaveError('Erreur lors de la sauvegarde du plan')
    } finally {
      setIsSaving(false)
    }
  }, [diagnosticId, planState, selectedSolutions, metierIds, navigate])

  const handleDownloadSynthese = useCallback(async () => {
    if (!diagnosticId) return
    trackEvent('telecharger pdf synthese')
    setIsDownloadingPDF(true)
    setPdfError(null)
    try {
      await api.downloadRapportPDF(diagnosticId)
    } catch (error: unknown) {
      console.error('Erreur téléchargement PDF:', error)
      const msg = error && typeof error === 'object' && 'message' in error
        ? (error as { message: string }).message
        : 'Erreur inconnue lors de la génération du PDF'
      setPdfError(msg)
    } finally {
      setIsDownloadingPDF(false)
    }
  }, [diagnosticId])

  if (isLoading) {
    return (
      <DiagnosticLayout
        steps={steps}
        showNavigation={false}
        diagnosticState={diagnosticState}
      >
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin w-10 h-10 border-3 border-primary border-t-transparent rounded-full" />
        </div>
      </DiagnosticLayout>
    )
  }

  const casques = result?.casques || 1
  const displayActionsCount = planState === 'exists' ? existingActionsCount : actionsCount

  return (
    <DiagnosticLayout
      steps={steps}
      showNavigation={false}
      diagnosticState={diagnosticState}
    >
      <div className="max-w-2xl mx-auto py-8 space-y-6">

        {/* ── Header : Check + Titre ── */}
        <div className="text-center space-y-3">

          {/* Check animé */}
          <div className={cn(
            'inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 transition-all duration-700 ease-out',
            show(1) ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
          )}>
            <CheckCircle2 className="size-10 text-primary" />
          </div>

          {/* Titre contextuel */}
          <h1 className={cn(
            'text-2xl sm:text-3xl font-bold text-foreground transition-all duration-500 ease-out',
            show(2) ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
          )}>
            {planState === 'empty' ? 'Diagnostic terminé !' : "Votre plan d'actions est prêt !"}
          </h1>

        </div>

        {/* ── Stats résumées — 3 chiffres ── */}
        <div className={cn(
          'transition-all duration-500 ease-out',
          show(4) ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
        )}>
          <Card>
            <CardContent className="p-5">
              <div className="grid grid-cols-3 divide-x divide-border">
                <StatBlock
                  value={themesCount || '\u2014'}
                  label={`thème${themesCount > 1 ? 's' : ''} prioritaire${themesCount > 1 ? 's' : ''}`}
                />
                <StatBlock
                  value={displayActionsCount || '\u2014'}
                  label={`action${displayActionsCount > 1 ? 's' : ''}`}
                />
                <div className="flex flex-col items-center justify-center px-4 py-2">
                  <CasqueIcons count={casques} size="sm" />
                  <p className="text-[11px] text-muted-foreground text-center leading-tight mt-1.5">
                    {casques} casque{casques !== 1 ? 's' : ''} sur 5
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── 2 cards : Plan d'action (héros 3/5) + Documents (2/5) ── */}
        <div className={cn(
          'grid grid-cols-1 sm:grid-cols-5 gap-4 transition-all duration-500 ease-out',
          show(5) ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
        )}>

          {/* Card Plan d'actions — héros */}
          <Card className="sm:col-span-3 border-primary/30 bg-primary/5">
            <CardContent className="p-6 flex flex-col items-center text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <ClipboardList className="size-6 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Plan d'actions</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {planState === 'empty'
                    ? 'Aucune action sélectionnée'
                    : `${displayActionsCount} action${displayActionsCount > 1 ? 's' : ''} à planifier`
                  }
                </p>
              </div>

              {/* CTA unique : sauvegarde silencieuse + redirection plan */}
              {planState !== 'empty' && (
                <>
                  <Button
                    onClick={handleGeneratePlan}
                    disabled={isSaving}
                    size="lg"
                    className="w-full max-w-xs"
                  >
                    {isSaving ? (
                      <><Loader2 className="size-4 mr-1 animate-spin" /> Chargement...</>
                    ) : (
                      <>Voir mon plan d'action <ArrowRight className="size-4 ml-1" /></>
                    )}
                  </Button>
                  {saveError && (
                    <p className="text-xs text-destructive flex items-center justify-center gap-1">
                      {!navigator.onLine && <WifiOff className="size-3" />}
                      {saveError}
                    </p>
                  )}
                </>
              )}

              {planState === 'empty' && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/diagnostic/${diagnosticId}/solutions`, {
                    state: {
                      ...diagnosticState,
                      selectedThemes,
                    },
                  })}
                  className="w-full max-w-xs"
                >
                  Choisir des solutions
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Card Synthèse PDF */}
          <Card className="sm:col-span-2">
            <CardContent className="p-6 flex flex-col items-center text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                {isDownloadingPDF ? (
                  <Loader2 className="size-6 text-muted-foreground animate-spin" />
                ) : (
                  <FileText className="size-6 text-muted-foreground" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Synthèse</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {isDownloadingPDF ? 'Génération en cours...' : 'Rapport complet de votre diagnostic'}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleDownloadSynthese}
                disabled={isDownloadingPDF}
                size="lg"
                className="w-full max-w-xs"
              >
                <FileText className="size-4 mr-1" />
                Télécharger le PDF
              </Button>
              {pdfError && (
                <p className="text-xs text-destructive">{pdfError}</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── Info attestation ── */}
        <div className={cn(
          'text-center pb-4 transition-all duration-500 ease-out',
          show(6) ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
        )}>
          <p className="text-sm text-muted-foreground">
            Votre attestation est disponible depuis le{' '}
            <button
              onClick={() => navigate('/dashboard/attestation')}
              className="text-primary hover:underline font-medium"
            >
              tableau de bord
            </button>
            .
          </p>
        </div>

      </div>
    </DiagnosticLayout>
  )
}
